/**
 * Affiliate Service
 * Handles all affiliate-related API calls
 * Optimized with proper error handling and response formatting
 */

import axios from 'axios';
import { getUID } from '@/utils/helper';

const API_BASE_URL = 'https://api.jackopot.com';

/**
 * Fetch referral info for current user
 * @returns {Promise<{referralCode: string, referralLink: string}>}
 */
export const fetchReferralInfo = async () => {
  const id = getUID();
  if (!id) {
    // Return empty object instead of throwing - allows component to handle gracefully
    return { referralCode: '', referralLink: '' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/affiliate/referral-info/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Return empty object for non-200 responses instead of throwing
      if (response.status === 404 || response.status === 401) {
        return { referralCode: '', referralLink: '' };
      }
      throw new Error(`Failed to fetch referral info: ${response.status}`);
    }

    const data = await response.json();
    return data || { referralCode: '', referralLink: '' };
  } catch (error) {
    console.error('Error fetching referral info:', error);
    // Return empty object instead of throwing to prevent query from failing
    return { referralCode: '', referralLink: '' };
  }
};

/**
 * Fetch team members for a referral code
 * @param {string} referralCode - Referral code
 * @returns {Promise<{teamMembers: Array}>}
 */
export const fetchTeamMembers = async (referralCode) => {
  try {
    if (!referralCode) {
      throw new Error('Referral code is required');
    }

    const response = await axios.get(`${API_BASE_URL}/affiliate/team/${referralCode}`);
    return {
      teamMembers: response.data.teamMembers || [],
      ...response.data,
    };
  } catch (error) {
    console.error('Error fetching team members:', error);
    throw error;
  }
};

/**
 * Fetch rewards for a referral code
 * @param {string} referralCode - Referral code
 * @returns {Promise<{rewards: Array, totalRewards: number}>}
 */
export const fetchRewards = async (referralCode) => {
  try {
    if (!referralCode) {
      throw new Error('Referral code is required');
    }

    const response = await axios.get(`${API_BASE_URL}/affiliate/rewards/${referralCode}`);
    return {
      rewards: response.data.rewards || [],
      totalRewards: response.data.totalRewards || 0,
      ...response.data,
    };
  } catch (error) {
    console.error('Error fetching rewards:', error);
    throw error;
  }
};

/**
 * Fetch unclaimed rewards for current user
 * @returns {Promise<{unclaimedRewards: Array, totalUnclaimed: number}>}
 */
export const fetchUnclaimedRewards = async () => {
  const id = getUID();
  if (!id) {
    // Return empty data instead of throwing
    return { unclaimedRewards: [], totalUnclaimed: 0 };
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/affiliate/unclaimed-rewards/${id}`);
    return {
      unclaimedRewards: response.data?.unclaimedRewards || [],
      totalUnclaimed: response.data?.totalUnclaimed || 0,
      ...(response.data || {}),
    };
  } catch (error) {
    console.error('Error fetching unclaimed rewards:', error);
    // Return empty data instead of throwing to prevent query from failing
    return { unclaimedRewards: [], totalUnclaimed: 0 };
  }
};

/**
 * Claim all rewards to wallet
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const claimAllRewards = async () => {
  try {
    const id = getUID();
    if (!id) {
      throw new Error('User ID not available');
    }

    const response = await axios.post(`${API_BASE_URL}/affiliate/claim-reward-all`, {
      uid: id,
    });

    return response.data;
  } catch (error) {
    console.error('Error claiming rewards:', error);
    throw error;
  }
};

/**
 * Fetch site config (affiliate bonus, commission percent)
 * @returns {Promise<{affiliatebonus: number, comissionpercent: number}>}
 */
export const fetchSiteConfig = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/siteconfig`);
    return response.data;
  } catch (error) {
    console.error('Error fetching site config:', error);
    throw error;
  }
};

/**
 * Calculate stats from rewards data
 * @param {Array} rewards - Rewards array
 * @param {number} totalMembers - Total team members
 * @param {number} totalRewards - Total rewards amount
 * @returns {Object} Calculated stats
 */
export const calculateRewardsStats = (rewards = [], totalMembers = 0, totalRewards = 0) => {
  const rewardsByType = rewards.reduce(
    (acc, reward) => {
      if (reward.type === 'referral') {
        acc.referralRewards += reward.amount || 0;
      } else if (reward.type === 'commission') {
        acc.commissionRewards += reward.amount || 0;
      }
      return acc;
    },
    { referralRewards: 0, commissionRewards: 0 }
  );

  const totalReferrals = rewards.reduce((acc, reward) => {
    if (reward.type === 'referral') {
      acc += reward.referalmount || 0;
    }
    return acc;
  }, 0);

  return {
    totalMembers,
    totalRewards,
    ...rewardsByType,
    totalReferrals,
  };
};
